import React, { useRef, useState, useEffect } from 'react';
import { Camera, Upload, Link as LinkIcon, X, Loader2, Image as ImageIcon, Clipboard, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { uploadImage, uploadImageFromUrl, imageUrl } from '../lib/api';
import { toast } from 'sonner';

const MAX_IMAGES = 5;

/**
 * Multi-mode image uploader for item photos.
 * Supports: file upload, camera capture, URL paste, clipboard paste (Ctrl+V).
 * Props:
 *   imageIds: string[] - current image ids
 *   onChange: (ids: string[]) => void
 *   max?: number (default 5)
 */
export const ImageUploader = ({ imageIds = [], onChange, max = MAX_IMAGES }) => {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const pasteZoneRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [pasteZoneFocused, setPasteZoneFocused] = useState(false);

  const canAddMore = imageIds.length < max;

  const handleUploadResult = (id) => {
    onChange([...imageIds, id]);
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.type.startsWith('image/'));
    if (!files.length) return;
    const slots = max - imageIds.length;
    const toUpload = files.slice(0, slots);
    if (files.length > slots) {
      toast.warning(`Only the first ${slots} image(s) added; max ${max} per item.`);
    }
    setUploading(true);
    try {
      for (const file of toUpload) {
        try {
          const res = await uploadImage(file);
          handleUploadResult(res.data.id);
        } catch (err) {
          toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    handleFiles(e.target.files);
    e.target.value = ''; // allow re-selecting same file
  };

  const handleUrlSubmit = async () => {
    const url = urlInput.trim();
    if (!url) return;
    if (!canAddMore) {
      toast.warning(`Max ${max} images per item.`);
      return;
    }
    setUploading(true);
    try {
      const res = await uploadImageFromUrl(url);
      handleUploadResult(res.data.id);
      setUrlInput('');
      setShowUrlInput(false);
      toast.success('Image added from URL');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch image from URL');
    } finally {
      setUploading(false);
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageItems = Array.from(items).filter(it => it.type.startsWith('image/'));
    if (!imageItems.length) return;
    e.preventDefault();
    if (!canAddMore) {
      toast.warning(`Max ${max} images per item.`);
      return;
    }
    const files = imageItems.map(it => it.getAsFile()).filter(Boolean);
    await handleFiles(files);
  };

  // Global paste listener when component is mounted and paste zone is focused
  useEffect(() => {
    const onGlobalPaste = (e) => {
      // Only intercept if no input/textarea is focused (or our paste zone is focused)
      const active = document.activeElement;
      const isEditable = active && (
        active.tagName === 'INPUT' ||
        active.tagName === 'TEXTAREA' ||
        active.isContentEditable
      );
      if (isEditable && active !== pasteZoneRef.current) return;
      handlePaste(e);
    };
    document.addEventListener('paste', onGlobalPaste);
    return () => document.removeEventListener('paste', onGlobalPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageIds]);

  const removeImage = (id) => {
    onChange(imageIds.filter(i => i !== id));
  };

  return (
    <div className="space-y-2" data-testid="image-uploader">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileInputChange}
        className="hidden"
        data-testid="image-file-input"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileInputChange}
        className="hidden"
        data-testid="image-camera-input"
      />

      {/* Thumbnails grid */}
      {imageIds.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="image-thumbs">
          {imageIds.map(id => (
            <div key={id} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-300 group bg-gray-50">
              <img src={imageUrl(id)} alt="" className="w-full h-full object-cover" data-testid={`thumb-${id}`} />
              <button
                type="button"
                onClick={() => removeImage(id)}
                className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-image-${id}`}
                aria-label="Remove image"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      {canAddMore && (
        <>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="h-9 rounded-lg gap-1.5"
              data-testid="upload-file-btn"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload size={14} />}
              Upload
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => cameraInputRef.current?.click()}
              className="h-9 rounded-lg gap-1.5"
              data-testid="camera-btn"
            >
              <Camera size={14} /> Camera
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => setShowUrlInput(v => !v)}
              className="h-9 rounded-lg gap-1.5"
              data-testid="paste-url-btn"
            >
              <LinkIcon size={14} /> URL
            </Button>
            <div
              ref={pasteZoneRef}
              tabIndex={0}
              onFocus={() => setPasteZoneFocused(true)}
              onBlur={() => setPasteZoneFocused(false)}
              onPaste={handlePaste}
              className={`h-9 px-3 rounded-lg border text-xs flex items-center gap-1.5 cursor-text outline-none transition-colors ${pasteZoneFocused ? 'border-[#0776BD] bg-[#E6F2FA] text-[#0776BD]' : 'border-gray-300 text-[#6B7280]'}`}
              data-testid="paste-zone"
              title="Click here then press Ctrl+V (or Cmd+V) to paste a screenshot"
            >
              <Clipboard size={14} />
              {pasteZoneFocused ? 'Press Ctrl+V to paste' : 'Paste'}
            </div>
          </div>

          {showUrlInput && (
            <div className="flex gap-2 items-center">
              <Input
                type="url"
                placeholder="https://example.com/image.jpg"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleUrlSubmit(); } }}
                className="h-9 rounded-lg border-gray-300 text-sm flex-1"
                data-testid="image-url-input"
              />
              <Button
                type="button"
                size="sm"
                onClick={handleUrlSubmit}
                disabled={uploading || !urlInput.trim()}
                className="h-9 rounded-lg bg-[#0776BD] hover:bg-[#055A92]"
                data-testid="submit-url-btn"
              >
                Add
              </Button>
            </div>
          )}

          <p className="text-xs text-[#9CA3AF]">
            {imageIds.length}/{max} photos · Upload, camera, URL, or paste from clipboard (Ctrl/Cmd+V)
          </p>
        </>
      )}
      {!canAddMore && (
        <p className="text-xs text-[#9CA3AF]">Max {max} photos reached. Remove one to add another.</p>
      )}
    </div>
  );
};

/**
 * Lightbox dialog showing an enlarged image with prev/next navigation.
 */
const ImageLightbox = ({ imageIds, startIndex, open, onOpenChange, alt }) => {
  const [index, setIndex] = useState(startIndex || 0);

  useEffect(() => {
    if (open) setIndex(startIndex || 0);
  }, [open, startIndex]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') setIndex(i => Math.max(0, i - 1));
      else if (e.key === 'ArrowRight') setIndex(i => Math.min(imageIds.length - 1, i + 1));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, imageIds.length]);

  if (!imageIds?.length) return null;
  const currentId = imageIds[index];
  const hasMultiple = imageIds.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl w-[92vw] p-0 pt-10 bg-black/95 border-none rounded-xl overflow-hidden"
        data-testid="image-lightbox"
      >
        <DialogTitle className="sr-only">{alt || 'Enlarged image'}</DialogTitle>
        <div className="relative flex items-center justify-center min-h-[55vh] max-h-[80vh] px-10">
          <img
            src={imageUrl(currentId)}
            alt={alt}
            className="max-w-full max-h-[80vh] object-contain rounded-lg"
            data-testid="lightbox-image"
          />

          {hasMultiple && (
            <>
              <button
                type="button"
                onClick={() => setIndex(i => Math.max(0, i - 1))}
                disabled={index === 0}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[#1F2937] rounded-full p-2 shadow-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                data-testid="lightbox-prev"
                aria-label="Previous image"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                type="button"
                onClick={() => setIndex(i => Math.min(imageIds.length - 1, i + 1))}
                disabled={index === imageIds.length - 1}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-[#1F2937] rounded-full p-2 shadow-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                data-testid="lightbox-next"
                aria-label="Next image"
              >
                <ChevronRight size={24} />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1 rounded-full">
                {index + 1} / {imageIds.length}
              </div>
            </>
          )}
        </div>

        {hasMultiple && (
          <div className="flex gap-2 justify-center py-3 px-4 overflow-x-auto bg-black/40">
            {imageIds.map((id, i) => (
              <button
                key={id}
                type="button"
                onClick={() => setIndex(i)}
                className={`w-14 h-14 rounded-md overflow-hidden border-2 shrink-0 transition-all ${i === index ? 'border-[#0776BD]' : 'border-transparent opacity-60 hover:opacity-100'}`}
                data-testid={`lightbox-thumb-${i}`}
                aria-label={`View image ${i + 1}`}
              >
                <img src={imageUrl(id)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Small thumbnail component for showing item images in tables/lists.
 * Click to open a lightbox. Shows placeholder icon when no images.
 */
export const ItemThumbnail = ({ imageIds = [], size = 40, alt = '' }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const firstId = imageIds?.[0];

  if (!firstId) {
    return (
      <div
        className="rounded-md bg-gray-100 flex items-center justify-center text-gray-400 shrink-0"
        style={{ width: size, height: size }}
        data-testid="item-thumb-placeholder"
      >
        <ImageIcon size={Math.round(size * 0.45)} />
      </div>
    );
  }

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setLightboxOpen(true); } }}
        className="rounded-md overflow-hidden border border-gray-200 shrink-0 bg-gray-50 hover:border-[#0776BD] hover:ring-2 hover:ring-[#0776BD]/30 transition-all cursor-zoom-in relative inline-block focus:outline-none focus:ring-2 focus:ring-[#0776BD]"
        style={{ width: size, height: size }}
        data-testid="item-thumb-btn"
        aria-label="Click to enlarge image"
      >
        <img
          src={imageUrl(firstId)}
          alt={alt}
          className="w-full h-full object-cover pointer-events-none"
          data-testid="item-thumb"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        {imageIds.length > 1 && (
          <span className="absolute bottom-0 right-0 bg-black/70 text-white text-[10px] leading-none px-1 py-0.5 rounded-tl-md">
            +{imageIds.length - 1}
          </span>
        )}
      </span>
      <ImageLightbox
        imageIds={imageIds}
        startIndex={0}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        alt={alt}
      />
    </>
  );
};

export default ImageUploader;
